import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, getFileUrl, type PageFile } from "@/lib/api";

const PAGE_META: Record<string, { title: string; placeholder: string }> = {
  about: {
    title: "협회소개",
    placeholder: "협회 소개 내용을 입력하세요.\n\n설립 목적, 주요 활동, 조직 구성 등을 자유롭게 작성해 주세요.",
  },
  events: {
    title: "행사일정",
    placeholder: "행사 일정을 입력하세요.\n\n예시)\n2026.08.02 - 한강 정기모임\n2026.08.22 - 산업용 드론 박람회 참관",
  },
  resources: {
    title: "자료실",
    placeholder: "자료실 내용을 입력하세요.\n\n공지사항, 규정집, 양식 등 유용한 자료를 등록해 주세요.",
  },
};

function isPdf(file: PageFile) {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isImage(file: PageFile) {
  return file.type.startsWith("image/");
}

export function PageContent() {
  const { slug } = useParams<{ slug: string }>();
  const { loggedIn } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [content, setContent] = useState("");
  const [files, setFiles] = useState<PageFile[]>([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editFiles, setEditFiles] = useState<PageFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const meta = slug ? PAGE_META[slug] : null;

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.pages.get(slug)
      .then((res) => {
        if (res.success && res.data) {
          setContent(res.data.content);
          setFiles(res.data.files ?? []);
          setUpdatedAt(res.data.updated_at);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  function startEdit() {
    setEditText(content);
    setEditFiles(files);
    setUploadError(null);
    setSaveError(null);
    setEditing(true);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;
    if (fileInputRef.current) fileInputRef.current.value = "";

    setUploading(true);
    setUploadError(null);

    for (const file of selected) {
      const res = await api.files.upload(file);
      if (res.success && res.data) {
        setEditFiles((prev) => [...prev, { key: res.data!.key, name: file.name, type: file.type }]);
      } else {
        setUploadError(`"${file.name}" 업로드 실패: ${res.error ?? "오류"}`);
      }
    }

    setUploading(false);
  }

  function removeEditFile(key: string) {
    setEditFiles((prev) => prev.filter((f) => f.key !== key));
  }

  async function handleSave() {
    if (!slug || saving) return;
    setSaving(true);
    setSaveError(null);
    const res = await api.pages.update(slug, editText, editFiles);
    setSaving(false);
    if (res.success && res.data) {
      setContent(res.data.content);
      setFiles(res.data.files ?? []);
      setUpdatedAt(res.data.updated_at);
      setEditing(false);
    } else {
      setSaveError(res.error ?? "저장 실패");
    }
  }

  if (!meta) return <div style={{ padding: 32 }}>존재하지 않는 페이지입니다.</div>;

  return (
    <main style={{ flex: 1, maxWidth: 860, margin: "0 auto", width: "100%", padding: 32 }}>
      {/* 페이지 헤더 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#0f2136" }}>{meta.title}</div>
        {loggedIn && !editing && (
          <button
            onClick={startEdit}
            style={{ background: "#3d7ab5", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            편집
          </button>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e6eb", borderRadius: 12, padding: 32 }}>
        {loading ? (
          <div style={{ color: "#a3adba", fontSize: 14, padding: "20px 0" }}>불러오는 중...</div>
        ) : editing ? (
          /* 편집 모드 */
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder={meta.placeholder}
              autoFocus
              style={{
                width: "100%",
                minHeight: 320,
                border: "1px solid #cfd6de",
                borderRadius: 8,
                padding: 14,
                fontSize: 14,
                lineHeight: 1.8,
                resize: "vertical",
                fontFamily: "inherit",
                color: "#1a2330",
                boxSizing: "border-box",
              }}
            />

            {/* 파일 첨부 영역 */}
            <div style={{ border: "1px dashed #c5cdd8", borderRadius: 10, padding: 18, background: "#f9fafc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: editFiles.length > 0 ? 14 : 0 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="page-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: uploading ? "#c5cdd8" : "#e8f0f9",
                    color: uploading ? "#8a9ab0" : "#3d7ab5",
                    border: "1px solid #c5d8ee",
                    borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                >
                  {uploading ? "업로드 중..." : "📎 파일 첨부 (이미지 · PDF)"}
                </button>
                <span style={{ fontSize: 12, color: "#9aa5b1" }}>여러 파일 동시 선택 가능</span>
              </div>

              {uploadError && (
                <div style={{ fontSize: 12.5, color: "#c0392b", marginBottom: 8 }}>{uploadError}</div>
              )}

              {/* 첨부된 파일 목록 */}
              {editFiles.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {editFiles.map((f) => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #e2e6eb", borderRadius: 8, padding: "8px 12px" }}>
                      {isImage(f) ? (
                        <img
                          src={getFileUrl(f.key)}
                          alt={f.name}
                          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{ width: 48, height: 48, background: "#fde9e9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                          📄
                        </div>
                      )}
                      <span style={{ flex: 1, fontSize: 13, color: "#3a4452", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeEditFile(f.key)}
                        style={{ background: "none", border: "none", color: "#b0bac5", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1, flexShrink: 0 }}
                        title="삭제"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {saveError && <div style={{ fontSize: 12.5, color: "#c0392b" }}>{saveError}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setEditing(false)}
                style={{ background: "#f5f7f9", color: "#6b7684", border: "1px solid #e2e6eb", borderRadius: 8, padding: "10px 20px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                style={{ background: (saving || uploading) ? "#7fa8cc" : "#3d7ab5", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13.5, fontWeight: 700, cursor: (saving || uploading) ? "not-allowed" : "pointer" }}
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        ) : (content || files.length > 0) ? (
          /* 보기 모드 — 내용 있음 */
          <div>
            {content && (
              <div style={{ fontSize: 14.5, color: "#3a4452", lineHeight: 1.9, whiteSpace: "pre-line", marginBottom: files.length > 0 ? 28 : 0 }}>
                {content}
              </div>
            )}

            {/* 첨부 파일 뷰어 */}
            {files.length > 0 && (
              <div>
                {content && <hr style={{ border: "none", borderTop: "1px solid #edf0f3", marginBottom: 24 }} />}

                {/* 이미지 그리드 */}
                {files.filter(isImage).length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: files.filter(isPdf).length > 0 ? 18 : 0 }}>
                    {files.filter(isImage).map((f) => (
                      <a key={f.key} href={getFileUrl(f.key)} target="_blank" rel="noopener noreferrer">
                        <img
                          src={getFileUrl(f.key)}
                          alt={f.name}
                          style={{ width: 180, height: 130, objectFit: "cover", borderRadius: 10, border: "1px solid #e2e6eb", display: "block" }}
                        />
                      </a>
                    ))}
                  </div>
                )}

                {/* PDF 목록 */}
                {files.filter(isPdf).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {files.filter(isPdf).map((f) => (
                      <a
                        key={f.key}
                        href={getFileUrl(f.key)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={f.name}
                        style={{ display: "flex", alignItems: "center", gap: 10, background: "#fef9f9", border: "1px solid #f0dada", borderRadius: 8, padding: "10px 14px", textDecoration: "none" }}
                      >
                        <span style={{ fontSize: 22 }}>📄</span>
                        <span style={{ fontSize: 13.5, color: "#c0392b", fontWeight: 600 }}>{f.name}</span>
                        <span style={{ marginLeft: "auto", fontSize: 12, color: "#b0bac5" }}>다운로드</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {updatedAt && (
              <div style={{ fontSize: 11.5, color: "#b8c0ca", marginTop: 24, textAlign: "right" }}>
                최종 수정: {updatedAt.slice(0, 10).replace(/-/g, ".")}
              </div>
            )}
          </div>
        ) : (
          /* 보기 모드 — 내용 없음 */
          <div style={{ textAlign: "center", padding: "48px 0", color: "#a3adba" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>아직 내용이 없습니다.</div>
            {loggedIn && (
              <div style={{ fontSize: 13, color: "#b8c0ca" }}>
                위의 <strong>편집</strong> 버튼을 눌러 내용을 작성해 주세요.
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
